import React, { useMemo, useState } from 'react';
import { ChevronRight, Download, Search, X } from 'lucide-react';
import { CardLegend } from './ReportCard';
import { formatValue } from './formatters';
import { SkeletonTableRows } from './ReportSkeleton';
import './ReportsNative.css';
import { ColumnPickerControl, applyColumnPrefs } from './ColumnPicker';
import { useFullscreenCard, FullscreenButton, FullscreenBackdrop } from './Fullscreen';

function buildPageList(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  const list = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  list.forEach((p) => {
    if (p - prev > 1) out.push('…');
    out.push(p);
    prev = p;
  });
  return out;
}

/**
 * ReportDetailTable — flat detail table (per financial-gl-detail.html
 * ledger + revenue-night-audits.html grouped link table): sortable
 * sticky header, optional grouped section header rows with count chips,
 * server-side pagination footer, optional sticky onyx-900 totals row.
 *
 * Props:
 *   title, pill, tools, legend  toolbar / bottom legend (like ReportMatrix)
 *   columns      [{ key, label, align?: 'left'|'right'|'center',
 *                   format?,            // mono numeral formatting
 *                   mono?, muted?, strong?,   // cell styling flags
 *                   sortable?, width?, render?(value, row) }]
 *   rows         flat row objects (cells read via row[col.key])
 *   groups       OPTIONAL grouped mode instead of rows:
 *                [{ key, label, count?, rows: [...] }] — renders onyx-50
 *                collapsible section header rows with count chips.
 *                A group may also carry `row`: an object rendered through the
 *                same columns as the data rows, so the header line shows the
 *                group's own figures (first column = twisty + label). Groups
 *                with no child rows render without a twisty.
 *   defaultCollapsedGroups  group keys to start collapsed (grouped mode);
 *                pass 'all' to start with every group closed
 *   groupControls  show "Expand all · Collapse all" links in the toolbar
 *                (grouped mode, default false)
 *   rowKey       (row, index) => key   (default row.id ?? index)
 *   onRowClick   (row) => void — row hover + pointer
 *   sort         { field, dir: 'asc'|'desc' }   (server-side, controlled)
 *   onSortChange ({ field, dir }) => void
 *   page, pageSize, totalRows, onPageChange     server pagination
 *   footNote     extra text in the pagination footer ('Showing 12 of 96 …')
 *   search       { value, onChange, placeholder } — search box in the toolbar.
 *                Optional multi-criteria mode (Journal Export-style): pass
 *                `onCommit()` (Enter pins the typed text as a criterion),
 *                `chips: [{ label }]`, `onRemoveChip(index)` and
 *                `onClearAll()` — the X then clears the text AND the chips.
 *   searchSlot   custom node rendered where the search box would be
 *   footer       { label?, cells: {} } sticky totals row
 *   currency, maxHeight (default 410 ≈ 10 rows), loading, emptyText
 */
export function ReportDetailTable({
  title,
  columnPrefs,
  pill,
  tools,
  legend,
  columns: columnsProp = [],
  rows,
  groups,
  defaultCollapsedGroups,
  groupControls = false,
  rowKey = (row, i) => row.id ?? i,
  onRowClick,
  selectedRowKey,
  sort,
  onSortChange,
  page,
  pageSize,
  totalRows,
  onPageChange,
  footNote,
  search,
  searchSlot,
  footer,
  currency,
  // ≈10 rows in view; scroll within — fullscreen unclamps
  maxHeight = 410,
  loading = false,
  emptyText = 'No rows match the current filters.',
  onDownloadCsv,
  fullscreenControl = true,
  className,
}) {
  const columns = applyColumnPrefs(columnsProp, columnPrefs);
  const currencyOpts = currency ? { currency } : undefined;
  const grouped = Array.isArray(groups);
  const [collapsed, setCollapsed] = useState(() =>
    defaultCollapsedGroups === 'all'
      ? new Set((groups || []).map((g) => g.key))
      : new Set(Array.isArray(defaultCollapsedGroups) ? defaultCollapsedGroups : [])
  );
  // Groups that arrive after mount (data refetch) honor the default too.
  const seenGroupsRef = React.useRef(new Set((groups || []).map((g) => g.key)));
  React.useEffect(() => {
    if (!grouped) return;
    const fresh = groups.filter((g) => !seenGroupsRef.current.has(g.key));
    if (!fresh.length) return;
    fresh.forEach((g) => seenGroupsRef.current.add(g.key));
    if (defaultCollapsedGroups === 'all') {
      setCollapsed((prev) => new Set([...prev, ...fresh.map((g) => g.key)]));
    } else if (Array.isArray(defaultCollapsedGroups)) {
      const wanted = fresh.filter((g) => defaultCollapsedGroups.includes(g.key)).map((g) => g.key);
      if (wanted.length) setCollapsed((prev) => new Set([...prev, ...wanted]));
    }
  }, [grouped, groups, defaultCollapsedGroups]);
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set((groups || []).map((g) => g.key)));

  const toggleGroup = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSort = (col) => {
    if (!col.sortable || !onSortChange) return;
    const dir = sort?.field === col.key && sort?.dir === 'desc' ? 'asc' : 'desc';
    onSortChange({ field: col.key, dir });
  };

  const cellClass = (col) => {
    const cls = [];
    if (col.align === 'right') cls.push('rn-align-right');
    if (col.align === 'center') cls.push('rn-align-center');
    if (col.mono || col.format) cls.push('rn-cell-mono');
    if (col.muted) cls.push('rn-cell-muted');
    if (col.strong) cls.push('rn-cell-strong');
    return cls.join(' ');
  };

  const renderCell = (col, row) => {
    const value = row[col.key];
    if (col.render) return col.render(value, row);
    if (col.format) return formatValue(value, col.format, currencyOpts);
    return value === null || value === undefined || value === '' ? '—' : value;
  };

  const totalPages = totalRows && pageSize ? Math.max(1, Math.ceil(totalRows / pageSize)) : null;
  const pageList = useMemo(
    () => (totalPages && page ? buildPageList(page, totalPages) : []),
    [page, totalPages]
  );
  const rangeStart = page && pageSize ? (page - 1) * pageSize + 1 : null;
  const flatCount = grouped ? groups.reduce((n, g) => n + (g.rows?.length || 0), 0) : rows?.length || 0;
  const rangeEnd = rangeStart !== null ? rangeStart + flatCount - 1 : null;

  const renderRows = (rowList, keyPrefix = '') =>
    rowList.map((row, i) => (
      <tr
        key={`${keyPrefix}${rowKey(row, i)}`}
        className={`${onRowClick ? 'rn-row-clickable' : ''}${selectedRowKey != null && String(rowKey(row, i)) === String(selectedRowKey) ? ' rn-row-selected' : ''}` || undefined}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
      >
        {columns.map((col) => (
          <td key={col.key} className={cellClass(col)} style={col.width ? { width: col.width } : undefined}>
            {renderCell(col, row)}
          </td>
        ))}
      </tr>
    ));

  const { isFullscreen, toggle: toggleFullscreen, exit: exitFullscreen } = useFullscreenCard();
  const showToolbar = title || pill || tools || search || searchSlot || onDownloadCsv || fullscreenControl || (grouped && groupControls);
  const empty = !loading && flatCount === 0;

  return (
    <>
      <FullscreenBackdrop active={isFullscreen} onClose={exitFullscreen} />
      <section className={`rn-card${isFullscreen ? ' rn-card-fs' : ''}${className ? ` ${className}` : ''}`}>
      {showToolbar && (
        <div className="rn-table-toolbar">
          {title && <h2>{title}</h2>}
          {pill && <span className="rn-pill-note">{pill}</span>}
          <div className="rn-spacer" />
          {searchSlot ??
            (search && (
              <div className="rn-search">
                <Search />
                {(search.chips || []).map((chip, i) => (
                  <span key={`${chip.label}-${i}`} className="rn-search-chip">
                    {chip.label}
                    {search.onRemoveChip && (
                      <button
                        type="button"
                        title={`Remove "${chip.label}"`}
                        aria-label={`Remove search criterion ${chip.label}`}
                        onClick={() => search.onRemoveChip(i)}
                      >
                        <X />
                      </button>
                    )}
                  </span>
                ))}
                <input
                  type="text"
                  value={search.value ?? ''}
                  onChange={(e) => search.onChange?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (!search.onCommit) return;
                    if (e.key === 'Enter' && String(search.value || '').trim()) {
                      e.preventDefault();
                      search.onCommit();
                    } else if (e.key === 'Backspace' && !search.value && search.chips?.length && search.onRemoveChip) {
                      search.onRemoveChip(search.chips.length - 1);
                    }
                  }}
                  placeholder={search.placeholder || 'Search…'}
                  aria-label={search.placeholder || 'Search'}
                  title={search.onCommit ? 'Press Enter to pin the text as a criterion and keep searching — rows must match every criterion.' : undefined}
                />
                {Boolean(search.value || search.chips?.length) && (
                  <button
                    type="button"
                    className="rn-search-clear"
                    title="Clear search"
                    aria-label="Clear search"
                    onClick={() => (search.onClearAll ? search.onClearAll() : search.onChange?.(''))}
                  >
                    <X />
                  </button>
                )}
              </div>
            ))}
          {tools}
          {grouped && groupControls && (
            <div className="rn-group-controls">
              <button type="button" className="rn-cell-link" onClick={expandAll} disabled={collapsed.size === 0}>
                Expand all
              </button>
              <span aria-hidden="true">·</span>
              <button type="button" className="rn-cell-link" onClick={collapseAll} disabled={collapsed.size === groups.length}>
                Collapse all
              </button>
            </div>
          )}
          {columnPrefs && <ColumnPickerControl columnPrefs={columnPrefs} />}
          {(onDownloadCsv || fullscreenControl) && (
            <div className="rn-tools">
              {onDownloadCsv && (
                <button type="button" className="rn-icon-btn" title="Download CSV" onClick={onDownloadCsv}>
                  <Download />
                </button>
              )}
              {fullscreenControl && <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />}
            </div>
          )}
        </div>
      )}

      {/* rn-scrollwrap anchors the mobile right-edge "more columns" fade */}
      <div className="rn-scrollwrap">
      <div className="rn-table-scroll" style={{ maxHeight: isFullscreen ? undefined : maxHeight }}>
        <table className="rn-detail-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const sorted = sort?.field === col.key;
                return (
                  <th
                    key={col.key}
                    className={[
                      col.align === 'right' ? 'rn-align-right' : col.align === 'center' ? 'rn-align-center' : '',
                      col.sortable && onSortChange ? 'rn-sortable' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => handleSort(col)}
                    aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {col.label}
                    {sorted && <span className="rn-sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="rn-sk-classic-only">
                <td colSpan={columns.length} style={{ padding: 0, border: 'none' }}>
                  <div className="lodge-skeleton rn-skeleton-block" />
                </td>
              </tr>
            )}
            {loading && <SkeletonTableRows columns={columns.length} rows={6} />}
            {empty && (
              <tr>
                <td colSpan={columns.length} style={{ border: 'none' }}>
                  <div className="rn-empty">{emptyText}</div>
                </td>
              </tr>
            )}
            {!loading && !empty && !grouped && renderRows(rows || [])}
            {!loading &&
              !empty &&
              grouped &&
              groups.map((group) => {
                const isCollapsed = collapsed.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    {group.row ? (
                      <tr
                        className={`rn-group-head rn-group-head-row${group.rows?.length ? '' : ' rn-group-head-leaf'}`}
                        onClick={group.rows?.length ? () => toggleGroup(group.key) : undefined}
                        aria-expanded={group.rows?.length ? !isCollapsed : undefined}
                      >
                        {columns.map((col, ci) => (
                          <td key={col.key} className={cellClass(col)} style={col.width ? { width: col.width } : undefined}>
                            {ci === 0 ? (
                              <>
                                {group.rows?.length ? (
                                  <span className={`rn-twisty${!isCollapsed ? ' rn-twisty-open' : ''}`}><ChevronRight size={13} aria-hidden="true" /></span>
                                ) : (
                                  <span className="rn-twisty-spacer" />
                                )}
                                {group.label}
                                {group.count != null && <span className="rn-count-chip">{group.count} {group.countLabel || 'reports'}</span>}
                              </>
                            ) : (
                              renderCell(col, group.row)
                            )}
                          </td>
                        ))}
                      </tr>
                    ) : (
                      <tr
                        className="rn-group-head"
                        onClick={() => toggleGroup(group.key)}
                        aria-expanded={!isCollapsed}
                      >
                        <td colSpan={columns.length}>
                          <span className={`rn-twisty${!isCollapsed ? ' rn-twisty-open' : ''}`}><ChevronRight size={13} aria-hidden="true" /></span>
                          {group.label}
                          {group.count != null && <span className="rn-count-chip">{group.count} {group.countLabel || 'reports'}</span>}
                        </td>
                      </tr>
                    )}
                    {!isCollapsed && renderRows(group.rows || [], `${group.key}-`)}
                  </React.Fragment>
                );
              })}
          </tbody>
          {footer && (
            <tfoot>
              <tr>
                {columns.map((col, i) => {
                  const value = footer.cells?.[col.key];
                  let content;
                  if (i === 0 && value === undefined) content = footer.label ?? 'Total';
                  else if (col.renderFooter) content = col.renderFooter(value, footer);
                  else if (value === undefined) content = null;
                  else if (col.format) content = formatValue(value, col.format, currencyOpts);
                  else content = value;
                  return (
                    <td key={col.key} className={cellClass(col)}>
                      {content}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </div>

      {(totalPages || footNote) && (
        <div className="rn-table-foot">
          {rangeStart !== null && totalRows != null && flatCount > 0 && (
            <span>
              Showing {rangeStart.toLocaleString()}–{Math.min(rangeEnd, totalRows).toLocaleString()} of{' '}
              {totalRows.toLocaleString()}
            </span>
          )}
          {footNote && <span>{footNote}</span>}
          <div className="rn-spacer" />
          {totalPages && totalPages > 1 && onPageChange && (
            <div className="rn-pager">
              <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                ‹
              </button>
              {pageList.map((p, i) =>
                p === '…' ? (
                  <button key={`e${i}`} type="button" className="rn-pager-ellipsis" disabled>
                    …
                  </button>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={p === page ? 'rn-active' : undefined}
                    onClick={() => onPageChange(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
                ›
              </button>
            </div>
          )}
        </div>
      )}

      {legend && <CardLegend items={legend} />}
      </section>
    </>
  );
}

export default ReportDetailTable;

/** In-cell % progress bar (per revenue-forecast.html "% to Budget" cells). */
export function PctProgressCell({ value, barMax = 100, decimals = 1 }) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return <span className="rn-cell-muted">—</span>;
  }
  const pct = Number(value);
  const width = Math.max(0, Math.min(100, (pct / barMax) * 100));
  return (
    <span className="rn-pct-cell">
      <span className="rn-pbar">
        <i style={{ width: `${width}%` }} />
      </span>
      {pct.toFixed(decimals)}%
    </span>
  );
}
